import type { FormEvent } from "react";
import { ArrowRight, Check, CreditCard, PackageCheck } from "lucide-react";
import type { OrganizationDetail } from "../../account";

const servicePlanFeatureLabels: Record<string, string> = {
  organization_profile: "Organisaatioprofiili",
  member_management: "Jäsenten hallinta",
  permit_sales: "Lupien myynti",
  financial_reports: "Talousraportit",
  integrations: "Integraatiot",
};

type OrganizationPackagePageProps = {
  assignedPlanFeatures: Set<string>;
  canAdmin: boolean;
  currentPlanLimits: Record<string, number>;
  data: OrganizationDetail;
  openBillingCheckout: () => void | Promise<void>;
  openBillingPortal: () => void | Promise<void>;
  pendingPlanChange: Record<string, unknown> | undefined;
  requestPlanChange: (event: FormEvent<HTMLFormElement>) => void;
};

export function OrganizationPackagePage({
  assignedPlanFeatures,
  canAdmin,
  currentPlanLimits,
  data,
  openBillingCheckout,
  openBillingPortal,
  pendingPlanChange,
  requestPlanChange,
}: OrganizationPackagePageProps) {
  return (
            <div className="workspace-content package-workspace">
              <section className="account-panel workspace-section package-summary">
                <div className="workspace-section-heading">
                  <div>
                    <span className="eyebrow">Palvelupaketti</span>
                    <h2>
                      {data.currentPlan
                        ? `${String(data.currentPlan.name)} v${String(data.currentPlan.plan_version)}`
                        : "Pakettia ei ole vielä liitetty"}
                    </h2>
                    <p>
                      Paketti määrittää työtilan ominaisuudet ja käyttörajat.
                      Maksullinen paketti aktivoituu vasta Stripen
                      allekirjoitetusta tilausvahvistuksesta.
                    </p>
                  </div>
                  <PackageCheck />
                </div>
                {data.currentPlan ? (
                  <>
                    <div className="package-price">
                      <strong>
                        {data.currentPlan.unit_amount === null
                          ? "Sopimushinta"
                          : (
                              Number(data.currentPlan.unit_amount) / 100
                            ).toLocaleString("fi-FI", {
                              style: "currency",
                              currency: String(data.currentPlan.currency),
                            })}
                      </strong>
                      <span>
                        {data.currentPlan.billing_interval === "month"
                          ? "kuukaudessa"
                          : data.currentPlan.billing_interval === "year"
                            ? "vuodessa"
                            : "sopimuksen mukaan"}{" "}
                        ·{" "}
                        {data.currentPlan.subscription_status === "active"
                          ? "aktiivinen"
                          : data.currentPlan.subscription_status === "trialing"
                            ? "kokeilujakso"
                            : data.currentPlan.subscription_status === "past_due"
                              ? "maksu myöhässä"
                              : data.currentPlan.subscription_status === "paused"
                                ? "keskeytetty"
                                : "odottaa laskutuksen avaamista"}
                      </span>
                    </div>
                    <p className="panel-copy">
                      {String(data.currentPlan.description)}
                    </p>
                    <div className="package-feature-list">
                      {Array.from(assignedPlanFeatures).map((feature) => (
                        <span key={feature}>
                          <Check />
                          {servicePlanFeatureLabels[feature] ??
                            `Muu ominaisuus (${feature})`}
                        </span>
                      ))}
                    </div>
                    {canAdmin &&
                      data.currentPlan.subscription_status === "pending" &&
                      Number(data.currentPlan.unit_amount ?? 0) > 0 && (
                        <div className="package-billing-actions">
                          <button
                            type="button"
                            disabled={
                              !data.billing.enabled ||
                              !data.currentPlan.stripe_price_reference
                            }
                            onClick={() => void openBillingCheckout()}
                          >
                            <CreditCard /> Aloita turvallinen tilaus
                          </button>
                          <small>
                            {!data.billing.enabled
                              ? "Stripe Billing ei ole vielä Platformissa käytössä."
                              : !data.currentPlan.stripe_price_reference
                                ? "Paketilta puuttuu Platformin Stripe Price -kytkentä."
                                : "Siirryt Stripen kassaan. Paluu sivulle ei yksin aktivoi pakettia."}
                          </small>
                        </div>
                      )}
                    {canAdmin &&
                      data.billing.customerLinked &&
                      data.billing.portalEnabled &&
                      ["trialing", "active", "past_due", "paused"].includes(
                        String(data.currentPlan.subscription_status),
                      ) && (
                        <div className="package-billing-actions">
                          <button
                            type="button"
                            className="secondary"
                            onClick={() => void openBillingPortal()}
                          >
                            Hallitse laskutusta Stripessä
                          </button>
                          <small>
                            Maksutapa, laskut ja tilauksen peruminen hoidetaan
                            Stripen asiakasportaalissa.
                          </small>
                        </div>
                      )}
                  </>
                ) : (
                  <div className="account-empty compact">
                    <PackageCheck />
                    <p>
                      Platform liittää palvelupaketin organisaation
                      käyttöönoton yhteydessä.
                    </p>
                  </div>
                )}
              </section>

              {data.currentPlan && (
                <section className="account-panel workspace-section">
                  <span className="eyebrow">Käyttöaste</span>
                  <h2>Kapasiteetti tällä hetkellä</h2>
                  <div className="package-usage-grid">
                    {[
                      {
                        key: "members",
                        label: "Jäsenet",
                        used: data.planUsage.members,
                      },
                      {
                        key: "areas",
                        label: "Alueet",
                        used: data.planUsage.areas,
                      },
                      {
                        key: "permitProducts",
                        label: "Lupatuotteet",
                        used: data.planUsage.permitProducts,
                      },
                    ].map((item) => {
                      const limit = currentPlanLimits[item.key],
                        percent =
                          limit === undefined || limit === 0
                            ? 0
                            : Math.min(100, (item.used / limit) * 100);
                      return (
                        <article key={item.key}>
                          <div>
                            <b>{item.label}</b>
                            <strong>
                              {item.used}/{limit ?? "∞"}
                            </strong>
                          </div>
                          <span>
                            <i style={{ width: `${percent}%` }} />
                          </span>
                          <small>
                            {limit !== undefined && item.used >= limit
                              ? "Raja on täynnä"
                              : "Kapasiteettia käytettävissä"}
                          </small>
                        </article>
                      );
                    })}
                  </div>
                </section>
              )}

              <section className="account-panel workspace-section">
                <span className="eyebrow">Paketin vaihto</span>
                <h2>Pyydä toinen palvelupaketti</h2>
                {pendingPlanChange ? (
                  <div className="application-status under_review">
                    <b>
                      Vaihtopyyntö: {String(pendingPlanChange.to_plan_name)} v
                      {String(pendingPlanChange.to_plan_version)}
                    </b>
                    <span>Odottaa Platformin käsittelyä</span>
                    {Boolean(pendingPlanChange.request_note) && (
                      <p>{String(pendingPlanChange.request_note)}</p>
                    )}
                  </div>
                ) : canAdmin && data.currentPlan ? (
                  <form
                    className="inline-account-form workspace-form"
                    onSubmit={requestPlanChange}
                  >
                    <label>
                      Uusi palvelupaketti
                      <select name="servicePlanId" required defaultValue="">
                        <option value="" disabled>
                          Valitse aktiivinen paketti
                        </option>
                        {data.servicePlans
                          .filter(
                            (plan) =>
                              String(plan.id) !== String(data.currentPlan?.id),
                          )
                          .map((plan) => (
                            <option value={String(plan.id)} key={String(plan.id)}>
                              {String(plan.name)} v{String(plan.plan_version)}
                            </option>
                          ))}
                      </select>
                    </label>
                    <label>
                      Perustelu <small>valinnainen</small>
                      <textarea
                        name="note"
                        maxLength={1000}
                        placeholder="Miksi pakettia halutaan vaihtaa?"
                      />
                    </label>
                    <p className="form-hint">
                      Platform tarkistaa käyttöasteen ennen hyväksyntää. Liian
                      pieneen pakettiin ei voi vaihtaa.
                    </p>
                    <div>
                      <button>
                        <ArrowRight /> Lähetä vaihtopyyntö
                      </button>
                    </div>
                  </form>
                ) : (
                  <div className="workspace-readonly">
                    Vain organisaation omistaja tai ylläpitäjä voi pyytää
                    paketin vaihtoa.
                  </div>
                )}
              </section>
            </div>
  );
}

